import { createContext, forwardRef, useContext, useState } from 'react';
import { Collapse } from '@figma/fpl-components';
import {
  Accordion as MuiAccordion,
  AccordionDetails as MuiAccordionDetails,
  AccordionSummary as MuiAccordionSummary,
} from '@mui/material';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import type { AccordionDetailsProps, AccordionProps, AccordionSummaryProps } from '@mui/material';
import type { ForwardedRef, MouseEvent, MouseEventHandler } from 'react';

interface FplAccordionContextValue {
  expanded: boolean;
  requestToggle: (event: MouseEvent<HTMLButtonElement>, summaryOnClick?: MouseEventHandler<HTMLButtonElement>) => void;
  unmountOnExit: boolean;
}

const FplAccordionContext = createContext<FplAccordionContextValue | null>(null);

function getDataAndAriaAttributes(props: object) {
  return Object.fromEntries(
    Object.entries(props).filter(([key]) => key.startsWith('data-') || key.startsWith('aria-')),
  );
}

const Accordion = forwardRef<HTMLDivElement, AccordionProps>(function Accordion(
  { children, defaultExpanded = false, expanded: controlledExpanded, onChange, TransitionProps, ...props },
  ref,
) {
  const isFplMigrationEnabled = useFplMigrationFlag();
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState(defaultExpanded);
  const isControlled = controlledExpanded !== undefined;
  const expanded = isControlled ? controlledExpanded : uncontrolledExpanded;

  if (!isFplMigrationEnabled) {
    return (
      <MuiAccordion
        {...props}
        ref={ref}
        defaultExpanded={defaultExpanded}
        expanded={controlledExpanded}
        onChange={onChange}
        TransitionProps={TransitionProps}
      >
        {children}
      </MuiAccordion>
    );
  }

  const { id, role, style, tabIndex, title } = props;
  const attributes = getDataAndAriaAttributes(props);
  const requestToggle = (
    event: MouseEvent<HTMLButtonElement>,
    summaryOnClick?: MouseEventHandler<HTMLButtonElement>,
  ) => {
    const nextExpanded = !expanded;
    if (!isControlled) {
      setUncontrolledExpanded(nextExpanded);
    }
    onChange?.(event, nextExpanded);
    summaryOnClick?.(event);
  };

  return (
    <FplAccordionContext.Provider
      value={{
        expanded,
        requestToggle,
        unmountOnExit: TransitionProps?.unmountOnExit === true,
      }}
    >
      <Collapse.Root
        {...attributes}
        ref={ref}
        id={id}
        role={role}
        style={style}
        tabIndex={tabIndex}
        title={title}
        isOpen={expanded}
        setOpen={(open) => {
          if (!isControlled) {
            setUncontrolledExpanded(open);
          }
        }}
      >
        {children}
      </Collapse.Root>
    </FplAccordionContext.Provider>
  );
});

const AccordionSummary = forwardRef<HTMLButtonElement, AccordionSummaryProps>(function AccordionSummary(
  { children, onClick, ...props },
  ref,
) {
  const fplAccordion = useContext(FplAccordionContext);

  if (!fplAccordion) {
    return (
      <MuiAccordionSummary {...props} ref={ref as unknown as ForwardedRef<HTMLDivElement>} onClick={onClick}>
        {children}
      </MuiAccordionSummary>
    );
  }

  const { autoFocus, disabled, id, role, style, tabIndex, title } = props;
  const attributes = getDataAndAriaAttributes(props);
  const fplOnClick = onClick as unknown as MouseEventHandler<HTMLButtonElement> | undefined;

  return (
    <Collapse.Header size="lg">
      <Collapse.Label
        {...attributes}
        ref={ref}
        autoFocus={autoFocus}
        disabled={disabled}
        id={id}
        onClick={(event) => fplAccordion.requestToggle(event, fplOnClick)}
        role={role}
        size="lg"
        style={style}
        tabIndex={tabIndex}
        title={title}
      >
        {children}
      </Collapse.Label>
    </Collapse.Header>
  );
});

const AccordionDetails = forwardRef<HTMLDivElement, AccordionDetailsProps>(function AccordionDetails(
  { children, role, style, title, ...props },
  ref,
) {
  const fplAccordion = useContext(FplAccordionContext);

  if (!fplAccordion) {
    return (
      <MuiAccordionDetails {...props} ref={ref} role={role} style={style} title={title}>
        {children}
      </MuiAccordionDetails>
    );
  }

  if (fplAccordion.unmountOnExit && !fplAccordion.expanded) {
    return null;
  }

  const attributes = getDataAndAriaAttributes(props);

  return (
    <Collapse.Content {...attributes} ref={ref} role={role} style={style} title={title}>
      {children}
    </Collapse.Content>
  );
});

export {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  type AccordionDetailsProps,
  type AccordionProps,
  type AccordionSummaryProps,
};
